import { Image, Skeleton } from "antd";
import PropTypes from "prop-types";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import ImageUpload from "../../../cleaner/components/ImageUpload";

const IMAGE_KEYS = ["image1", "image2", "image3", "image4"];
const SQUARE_IMG_CLASS = "rounded-none object-cover bg-[#f2f2fa]";

export function imagesToFileList(images = {}) {
    return IMAGE_KEYS.map((key, index) => {
        const url = images[key];
        if (!url) return null;
        return {
            uid: `-${index + 1}`,
            name: key,
            status: "done",
            url,
        };
    }).filter(Boolean);
}

export function fileListToImages(fileList) {
    const result = { image1: "", image2: "", image3: "", image4: "" };
    fileList.forEach((file, index) => {
        const key = IMAGE_KEYS[index];
        if (!key) return;
        if (file.originFileObj) {
            result[key] = file.originFileObj;
        } else if (file.url) {
            result[key] = file.url;
        }
    });
    return result;
}

function CarImagesGallery({ images = {}, isEdit = false, loading = false, onChange, fallbackImage }) {
    const { t } = useTranslation("cars");
    const [fileList, setFileList] = useState(() => imagesToFileList(images));

    useEffect(() => {
        if (!isEdit) return;
        setFileList(imagesToFileList(images));
    }, [images, isEdit]);

    const displayUrls = useMemo(() => {
        if (isEdit) {
            return fileList.map((file) => file.url || (file.originFileObj ? URL.createObjectURL(file.originFileObj) : null));
        }
        const fromCar = IMAGE_KEYS.map((key) => images[key]).filter(Boolean);
        if (fromCar.length > 0) return fromCar;
        return fallbackImage ? [fallbackImage] : [];
    }, [fileList, images, isEdit, fallbackImage]);

    const handleFileChange = (newFileList) => {
        setFileList(newFileList);
        onChange?.(fileListToImages(newFileList));
    };

    if (loading) {
        return (
            <div className="grid grid-cols-2 gap-2 px-4 py-3">
                {[1, 2, 3, 4].map((i) => (
                    <Skeleton.Image key={i} active className="!h-[7.5rem] !w-full !rounded-none" />
                ))}
            </div>
        );
    }

    if (isEdit) {
        return (
            <div className="border-b border-[#f0f0f0] px-4 py-3">
                <p className="mb-2 text-sm font-medium text-[#121212]">{t("uploadImages")}</p>
                <ImageUpload fileList={fileList} handleFileChange={handleFileChange} maxCount={4} />
            </div>
        );
    }

    if (displayUrls.length === 0) {
        return (
            <div className="px-4 py-3">
                <div className="flex aspect-[4/3] w-full items-center justify-center rounded-none border border-dashed border-[#e5e5e5] bg-[#fafafa] text-sm text-[#acacb9]">
                    {t("notAvailable")}
                </div>
            </div>
        );
    }

    return (
        <div className="border-b border-[#f0f0f0] px-4 py-3">
            <Image.PreviewGroup>
                <div
                    className={`grid gap-2 ${
                        displayUrls.length === 1 ? "grid-cols-1" : "grid-cols-2"
                    }`}>
                    {displayUrls.map((url) => (
                        <Image
                            key={url}
                            src={url}
                            alt=""
                            className={`${SQUARE_IMG_CLASS} !h-[7.5rem] w-full cursor-pointer`}
                            preview={{ mask: t("view") }}
                            rootClassName="w-full"
                        />
                    ))}
                </div>
            </Image.PreviewGroup>
        </div>
    );
}

CarImagesGallery.propTypes = {
    images: PropTypes.shape({
        image1: PropTypes.string,
        image2: PropTypes.string,
        image3: PropTypes.string,
        image4: PropTypes.string,
    }),
    isEdit: PropTypes.bool,
    loading: PropTypes.bool,
    onChange: PropTypes.func,
    fallbackImage: PropTypes.string,
};

export default CarImagesGallery;
